import { useState } from 'react';
import { ScoreboardWidget } from '../../components/broadcaster/ScoreboardWidget';
import { StockManager } from '../../components/broadcaster/StockManager';
import { AbilityToaster } from '../../components/broadcaster/AbilityToaster';

// Mock Data Types for Prototype
export interface PlayerState {
    name: string;
    score: number;
    stocks: boolean[]; // true = alive, false = dead
    characterId: string;
}

export default function ControlCenterPrototype() {
    // --- Local State (Simulating Firestore) ---
    const [p1, setP1] = useState<PlayerState>({
        name: 'Skybound',
        score: 0,
        stocks: [true, true, true],
        characterId: 'ness' // mock
    });

    const [p2, setP2] = useState<PlayerState>({
        name: 'Cloud9',
        score: 0,
        stocks: [true, true, true],
        characterId: 'mario' // mock
    });

    const [round, setRound] = useState('Grand Finals');
    const [activeToasts, setActiveToasts] = useState<string[]>([]);

    // --- Handlers ---
    const toggleStock = (player: 'p1' | 'p2', index: number) => {
        const setter = player === 'p1' ? setP1 : setP2;
        setter(prev => {
            const newStocks = [...prev.stocks];
            newStocks[index] = !newStocks[index];
            return { ...prev, stocks: newStocks };
        });
    };

    const updateScore = (player: 'p1' | 'p2', delta: number) => {
        const setter = player === 'p1' ? setP1 : setP2;
        setter(prev => ({ ...prev, score: Math.max(0, prev.score + delta) }));
    };

    const handeToastAbility = (abilityName: string) => {
        // Simulate "Toasting"
        setActiveToasts(prev => [...prev, abilityName]);
        setTimeout(() => {
            setActiveToasts(prev => prev.filter(t => t !== abilityName));
        }, 3000); // Auto-dismiss after 3s mock
    };

    return (
        <div className="h-fullflex flex-col md:flex-row p-4 gap-4 overflow-y-auto">
            {/* LEFT COLUMN: Match Control (Score & Stocks) */}
            <div className="flex-1 space-y-4">
                <section className="bg-slate-900 rounded-2xl p-4 border border-slate-800 shadow-xl">
                    <h2 className="text-slate-400 font-bold mb-4 uppercase tracking-wider text-sm">Match Status</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Player 1 Control */}
                        <div className="bg-slate-950/50 p-4 rounded-xl border border-blue-900/30">
                            <input
                                value={p1.name}
                                onChange={(e) => setP1({ ...p1, name: e.target.value })}
                                className="w-full bg-transparent text-2xl font-bold text-blue-400 border-b border-blue-900/50 focus:border-blue-500 outline-none mb-4 placeholder-blue-900/50"
                                placeholder="Player 1"
                            />

                            <ScoreboardWidget score={p1.score} onUpdate={(d) => updateScore('p1', d)} color="blue" />

                            <div className="mt-4 pt-4 border-t border-slate-900">
                                <p className="text-xs text-slate-500 mb-2 font-bold uppercase">Stocks Overview</p>
                                <StockManager stocks={p1.stocks} onToggle={(i) => toggleStock('p1', i)} color="blue" />
                            </div>
                        </div>

                        {/* Player 2 Control */}
                        <div className="bg-slate-950/50 p-4 rounded-xl border border-red-900/30">
                            <input
                                value={p2.name}
                                onChange={(e) => setP2({ ...p2, name: e.target.value })}
                                className="w-full bg-transparent text-2xl font-bold text-red-400 border-b border-red-900/50 focus:border-red-500 outline-none mb-4 text-right placeholder-red-900/50"
                                placeholder="Player 2"
                            />

                            <ScoreboardWidget score={p2.score} onUpdate={(d) => updateScore('p2', d)} color="red" align="right" />

                            <div className="mt-4 pt-4 border-t border-slate-900">
                                <p className="text-xs text-slate-500 mb-2 font-bold uppercase text-right">Stocks Overview</p>
                                <StockManager stocks={p2.stocks} onToggle={(i) => toggleStock('p2', i)} color="red" align="right" />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Round Info */}
                <section className="bg-slate-900 rounded-2xl p-4 border border-slate-800 flex items-center gap-4">
                    <span className="text-slate-500 font-bold uppercase text-xs">Round:</span>
                    <input
                        value={round}
                        onChange={(e) => setRound(e.target.value)}
                        className="flex-1 bg-slate-950 p-3 rounded-lg text-lg font-bold text-white border border-slate-700"
                    />
                    <button className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 px-6 rounded-lg active:scale-95 transition-transform">
                        UPDATE STREAM
                    </button>
                </section>
            </div>

            {/* RIGHT COLUMN: Ability Toaster & Quick Actions */}
            <div className="w-full md:w-96 space-y-4">
                <section className="bg-slate-900 rounded-2xl p-4 border border-slate-800 h-full flex flex-col">
                    <h2 className="text-slate-400 font-bold mb-4 uppercase tracking-wider text-sm">Ability Toaster</h2>
                    <div className="flex-1">
                        <AbilityToaster characterId={p1.characterId} onToast={handeToastAbility} />
                    </div>

                    {/* Active Toasts Debug Preview */}
                    <div className="mt-4 p-2 bg-black/50 rounded text-xs font-mono text-green-400 min-h-[3rem]">
                        Toasts: {activeToasts.join(', ') || 'None'}
                    </div>
                </section>
            </div>
        </div>
    );
}
